import { TranscriptEditorCore } from './TranscriptEditorCore';
import { TranscriptElement } from './models/TranscriptElement';
import { TextElement, PunctElement } from './models/TranscriptElement';

/**
 * Types of changes that can occur in the transcript editor
 */
export enum TranscriptEditorChangeType {
  TRANSCRIPT_LOADED = 'transcript_loaded',
  TRANSCRIPT_CHANGED = 'transcript_changed',
  TRANSCRIPT_MODIFIED = 'transcript_modified',
  CHUNK_CHANGED = 'chunk_changed',
  LANGUAGE_CHANGED = 'language_changed',
  SPEAKER_ASSIGNED = 'speaker_assigned',
  SPEAKER_REMOVED = 'speaker_removed',
  SPEAKER_MAPPINGS_LOADED = 'speaker_mappings_loaded',
  OPERATION_APPLIED = 'operation_applied',
  ACTIVE_CHUNK_CHANGED = 'active_chunk_changed',
  ACTIVE_SEGMENT_CHANGED = 'active_segment_changed',
  ELEMENT_UPDATED = 'element_updated',
  ELEMENT_DELETED = 'element_deleted',
  ELEMENT_ADDED = 'element_added',
  ELEMENTS_DELETED = 'elements_deleted',
  PARAGRAPH_ELEMENTS_UPDATED = 'paragraph_elements_updated',
  PARAGRAPH_SPLIT = 'paragraph_split',
  PARAGRAPH_MERGED = 'paragraph_merged',
  PARAGRAPH_MOVED = 'paragraph_moved',
  PARAGRAPH_DELETED = 'paragraph_deleted',
  SEGMENT_CREATED = 'segment_created',
  SEGMENT_DELETED = 'segment_deleted',
  SEGMENTS_MERGED = 'segments_merged',
  BATCH_COMPLETED = 'batch_completed',
  CHANGES_SAVED = 'changes_saved',
  CHANGES_DISCARDED = 'changes_discarded',
  ERROR_OCCURRED = 'error_occurred',
  SELECTION_CHANGED = 'selection_changed',
  UNTIMED_TEXT_ADDED = 'untimed_text_added',
  UNTIMED_TEXT_PROCESSED = 'untimed_text_processed',
  TEXT_REPLACED_GLOBAL = 'text_replaced_global',
  TEXT_REPLACED_SELECTION = 'text_replaced_selection',
  POST_HOOKS_APPLIED = 'post_hooks_applied',
}

/**
 * Observer interface for the transcript editor
 */
export interface TranscriptEditorObserver {
  onEditorChange: (
    editor: TranscriptEditorCore,
    changeType: TranscriptEditorChangeType,
    payload?: any
  ) => void;
}

/**
 * Resources associated with a transcript
 */
export interface TranscriptResources {
  audioUrl: string;
  transcriptionUrl: string;
  waveformDataUrl: string;
  thumbnailUrl: string;
  speakerMappingsUrl: string;
  chunkListUrl: string;
  containerManifestUrl: string;
  originalManifestUrl: string;
  originalChunkListUrl: string;
  // Languages available for the transcript
  languages?: string[];
  chunks: {
    list: {
      chunks: Array<{
        id: string;
        startTime: number;
        endTime: number;
        path: string;
        language?: string;
      }>;
      language: string;
    };
    urls: Record<string, string>;
  };
  translations?: {
    availableLanguages: string[];
    urls: Record<string, string>;
  };
  exists: {
    audio: boolean;
    transcription: boolean;
    waveform: boolean;
    thumbnail: boolean;
    speakerMappings: boolean;
    chunkList: boolean;
    containerManifest: boolean;
  };
}

/**
 * Speaker mappings for a transcript
 * 
 * This is the central mapping between speaker labels and speaker IDs:
 * - Keys are speaker labels (always "spk_X" format) used in transcript segments
 * - Values are Dato CMS speaker IDs (never start with "spk_")
 * 
 * See detailed documentation in:
 * - /transcript/services/SpeakerService.ts
 * - /transcript/models/TranscriptSpeaker.ts
 * - /transcript/TranscriptEditorStore.ts
 */
export interface TranscriptVoiceMapping {
  storyId: string;
  mappings: { [speakerLabel: string]: string | undefined };
  lastUpdated: number;
}

/**
 * Edit operation on a transcript
 */
export interface EditOperation {
  type: 'edit' | 'merge' | 'split' | 'speaker_assignment' | 'text_edit' | 'paragraph_split' | 'paragraph_merge' | 'paragraph_move' | 'segment_create' | 'paragraph_elements_update' | 'element_delete' | 'elements_delete' | 'paragraph_delete' | 'global_text_replace';
  chunkId?: string;
  elementId?: string;
  segmentId?: string;
  paragraphId?: string;
  data: any;
  userId: string;
  timestamp: number;
}

/**
 * Command types for transcript editor
 */
export enum TranscriptCommandType {
  UPDATE_TEXT = 'update_text',
  ASSIGN_SPEAKER = 'assign_speaker',
  ASSIGN_SPEAKER_GLOBAL = 'assign_speaker_global',
  SPLIT_PARAGRAPH = 'split_paragraph',
  MERGE_PARAGRAPHS = 'merge_paragraphs',
  CREATE_SEGMENT = 'create_segment',
  MOVE_PARAGRAPH = 'move_paragraph',
  DELETE_ELEMENT = 'delete_element',
  DELETE_ELEMENTS = 'delete_elements',
  DELETE_PARAGRAPH = 'delete_paragraph',
  REPLACE_TEXT_GLOBAL = 'replace_text_global',
  REPLACE_TEXT_SELECTION = 'replace_text_selection'
}

/**
 * Transcript data
 */
export interface TranscriptData {
  elements: TranscriptElement[];
  fullText: string;
  language: string;
}

/**
 * Selected transcript element (legacy)
 */
export interface SelectedTranscriptElement {
  chunkId?: string;
  elementId: string;
  element: TranscriptElement | TextElement | PunctElement;
  segmentId?: string;
  paragraphId?: string;
}

/**
 * User identity type
 */
export interface UserIdentity {
  id: string;
  name?: string;
  email?: string;
}

/**
 * Represents a selected element for batch operations
 */
export interface ElementSelection {
  segmentId: string;
  paragraphId: string;
  elementId: string;
  element?: TextElement | PunctElement;
}

/**
 * Represents a pending text edit operation for debouncing
 */
export interface PendingTextEdit {
  segmentId: string;
  paragraphId: string;
  elementId: string;
  text: string;
  userId: string;
  timestamp: number;
  noTimingData?: boolean;
}

/**
 * Represents new text that needs timing data
 */
export interface UntimingText {
  segmentId: string;
  paragraphId: string;
  elementId: string;
  text: string;
  position: 'start' | 'end' | 'middle';
  
  // Context for the alignment service
  timeContext: {
    // Boundaries for alignment service (including 3s buffer)
    alignmentStart: number; 
    alignmentEnd: number;
    
    // Original boundaries of the untimed text
    originalStart: number;
    originalEnd: number;
    
    // Known words before/after the untimed text (for alignment)
    contextWordsBefore: Array<{
      text: string;
      startTime: number;
      endTime: number;
      elementId: string;
    }>;
    
    contextWordsAfter: Array<{
      text: string;
      startTime: number;
      endTime: number;
      elementId: string;
    }>;
  };
}

/**
 * Represents a word with timing information for alignment service
 */
export interface TimedWord {
  word: string;
  start: number;
  end: number;
  case: 'success' | 'not-found';
  confidence?: number;
  elementId?: string;
}

/**
 * Represents a response from the alignment service
 */
export interface AlignmentServiceResponse {
  words: TimedWord[];
  engine: string;
  clip_duration: number;
}

/**
 * Standardized speaker information interface
 * 
 * Used throughout the application to represent speaker data
 * consistently with clear terminology:
 * 
 * - speakerLabel: The anonymous "spk_X" identifier used in transcript segments
 * - datoSpeakerId: The ID from DatoCMS for named speakers (optional)
 * - name: Display name for the speaker
 * - color: Visual color for the speaker (optional)
 */
export interface SpeakerInfo {
  speakerLabel: string;       // The anonymous "spk_X" label
  datoSpeakerId?: string;     // Optional DatoCMS ID
  name: string;               // Display name 
  color?: string;             // Optional color
}